import {FormEvent, useEffect, useRef, useState} from 'react';
import './AttorneyMattersDashboard.css';
import {DashboardShell} from './DashboardShell';
import {claimAttorneyMatter, listClaimedAttorneyMatters, type ClaimedMatter, FenzSafeAttorneyMatterClaimError} from './fenzsafeAttorneyMatterClaims';

const SHARE_TOKEN_PATTERN = /[a-f0-9]{64}/;

/**
 * Extracts a 64-hex-character share token from either a full share URL
 * (`.../fenzsafe/share/<token>`) or the bare token itself, as an attorney might paste either.
 * Returns null for anything that doesn't contain a recognizable token, so the caller can show a
 * clear client-side error rather than sending a request guaranteed to 404.
 */
function extractShareToken(input: string): string | null {
  const match = SHARE_TOKEN_PATTERN.exec(input.trim());
  return match ? match[0] : null;
}

const NAV_ITEMS = [{id: 'matters', label: 'Matters'}];

/** e.g. "2026-09-22T00:00:00Z" -> "Sep 22, 2026", falling back to the raw value if it doesn't parse. */
function formatClaimedDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'});
}

function statusLabel(status: ClaimedMatter['packageStatus']): string {
  if (status === 'revoked') return 'Access revoked';
  if (status === 'opened') return 'Opened';
  return 'Shared';
}

/**
 * B57 (Issue #192 restart, docs/ai/INFORMATION_DENSITY_UX_PROPOSAL.md): wraps this dashboard in the
 * B56 DashboardShell -- a compact status-card row, a scannable matters table, and the claim form
 * moved from an always-visible inline block to the shell's primary-action slot (progressive
 * disclosure), per the founder's density direction. Underlying data/claim logic is unchanged.
 *
 * The attorney's "elegant matter-centered dashboard" -- deliberately the smallest version: a list
 * of matters the signed-in caller's own organization has claimed, each linking back to the exact
 * same real, already-authorized public share view (`/fenzsafe/share/{token}`) rather than
 * inventing a second, authenticated matter view -- the underlying data (documents, Matter Summary,
 * evidence requests) already lives there and this session is not duplicating it.
 *
 * Claiming happens entirely from this authenticated surface, not from the public share page
 * (Issue #192 watchdog correction, comment `5780822783`): `FenzSafeShareView.tsx` stays purely
 * public/token-scoped and makes no session-probing request of any kind. An attorney who received a
 * share link pastes it (or its bare token) here; the claiming organization/identity is always
 * derived from the authenticated session that submits it, never from anything supplied in the
 * input -- the pasted text only ever supplies which matter, never who is claiming it.
 *
 * `claimsReady` (Issue #126 comment `5825434020`'s live-wiring assignment, `fenzsafe_attorney_
 * claims_ready`): reflects whether profenz_fenzsafe_attorney_package_claims is actually installed.
 * Before this, the claim form and matter list always rendered/fetched regardless of that table's
 * presence -- the one FenzSafe capability with no readiness gate at all. Defaults to `false`
 * (STOP/CORRECT, PR #225 comment `5826167082`): an omitted prop must fail closed like every other
 * FenzSafe readiness contract, not assume readiness, so a future caller that forgets to pass it
 * can't render the claim form or fire its fetch against an uninstalled table. The real mount
 * (Application.tsx) always supplies the real, honest value read from the authenticated
 * application context; tests that want the ready path pass `claimsReady={true}` explicitly.
 */
export function AttorneyMattersDashboard({claimsReady = false}: {claimsReady?: boolean} = {}) {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [matters, setMatters] = useState<ClaimedMatter[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [claimFormOpen, setClaimFormOpen] = useState(false);
  const claimPending = useRef(false);
  const [claimInput, setClaimInput] = useState('');
  const [claimStatus, setClaimStatus] = useState<'idle' | 'claiming' | 'error'>('idle');
  const [claimError, setClaimError] = useState('');
  const [claimedMessage, setClaimedMessage] = useState('');

  async function refresh(): Promise<void> {
    setState('loading');
    try {
      const items = await listClaimedAttorneyMatters();
      setMatters(items);
      setState('ready');
    } catch (error) {
      setErrorMessage(error instanceof FenzSafeAttorneyMatterClaimError ? error.message : 'Your matters could not be loaded.');
      setState('error');
    }
  }

  useEffect(() => {
    if (!claimsReady) return;
    void refresh();
  }, [claimsReady]);

  async function handleClaimSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!claimsReady || claimPending.current) return;
    const token = extractShareToken(claimInput);
    if (token === null) {
      setClaimStatus('error');
      setClaimError('That doesn’t look like a FenzSafe share link. Paste the full link or its code from your client.');
      return;
    }
    claimPending.current = true;
    setClaimStatus('claiming');
    setClaimError('');
    setClaimedMessage('');
    try {
      await claimAttorneyMatter(token);
      setClaimInput('');
      setClaimedMessage('Added to your firm’s matters.');
      await refresh();
      setClaimStatus('idle');
    } catch (error) {
      setClaimStatus('error');
      setClaimError(error instanceof FenzSafeAttorneyMatterClaimError ? error.message : 'This could not be added to your firm’s matters right now.');
    } finally {
      claimPending.current = false;
    }
  }

  function closeClaimForm() {
    if (claimPending.current) return;
    setClaimFormOpen(false);
    setClaimInput('');
    setClaimStatus('idle');
    setClaimError('');
    setClaimedMessage('');
  }

  if (!claimsReady) {
    return <p role="status">Matters shared with your firm aren’t available in this environment yet.</p>;
  }

  const sharedCount = matters.filter((matter) => matter.packageStatus === 'shared').length;

  const claimForm = (
    <form className="attorney-matters-claim-form" onSubmit={(event) => void handleClaimSubmit(event)}>
      <label htmlFor="fenzsafe-claim-input">Add a matter your client shared with you</label>
      <input
        id="fenzsafe-claim-input"
        type="text"
        value={claimInput}
        disabled={claimStatus === 'claiming'}
        onChange={(event) => { if (!claimPending.current) setClaimInput(event.target.value); }}
        placeholder="Paste the share link your client sent you"
      />
      <div className="attorney-matters-claim-form-actions">
        <button type="submit" disabled={claimStatus === 'claiming' || claimInput.trim() === ''}>
          {claimStatus === 'claiming' ? 'Adding…' : 'Add matter'}
        </button>
        <button type="button" disabled={claimStatus === 'claiming'} onClick={closeClaimForm}>
          Cancel
        </button>
      </div>
      {claimStatus === 'error' && <p role="alert">{claimError}</p>}
      {claimedMessage !== '' && claimStatus === 'idle' && <p role="status">{claimedMessage}</p>}
    </form>
  );

  return (
    <DashboardShell
      shellLabel="Attorney workspace"
      navItems={NAV_ITEMS}
      activeNavId="matters"
      onNavSelect={() => {}}
      title="Matters"
      primaryAction={{label: 'Claim a new matter', disabled: claimStatus === 'claiming', onClick: () => { if (!claimPending.current) setClaimFormOpen(true); }}}
    >
      <div className="attorney-matters-dashboard">
        {claimFormOpen && claimForm}

        {state === 'loading' && <p role="status">Loading your matters…</p>}
        {state === 'error' && <p role="alert">{errorMessage}</p>}

        {state === 'ready' && (
          <>
            <div className="attorney-matters-stats" role="group" aria-label="Matter summary">
              <div className="attorney-matters-stat">
                <span className="attorney-matters-stat-num">{matters.length}</span>
                <span className="attorney-matters-stat-label">Matters</span>
              </div>
              <div className="attorney-matters-stat">
                <span className="attorney-matters-stat-num">{sharedCount}</span>
                <span className="attorney-matters-stat-label">Shared packages</span>
              </div>
            </div>

            {matters.length === 0 ? (
              <p role="status">No matters yet. Use “Claim a new matter” above to add one using a share link your client sent you.</p>
            ) : (
              <table className="attorney-matters-table" aria-label="Matters shared with your firm">
                <thead>
                  <tr>
                    <th scope="col">Matter</th>
                    <th scope="col">Reference</th>
                    <th scope="col">Claimed</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {matters.map((matter) => (
                    <tr key={matter.claimId}>
                      <td>{matter.packageStatus === 'revoked' ? matter.matterName : <a href={`/fenzsafe/share/${matter.shareToken}`}>{matter.matterName}</a>}</td>
                      <td>{matter.matterReference}</td>
                      <td>{formatClaimedDate(matter.claimedAt)}</td>
                      <td>{statusLabel(matter.packageStatus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
}
